import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Collapse, Chip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import {
  CloudUpload as UploadIcon, Download as DownloadIcon, CheckCircle as SuccessIcon,
  Warning as WarningIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  Description as TemplateIcon, Backup as BackupIcon, Info as InfoIcon
} from '@mui/icons-material';
import { useAppContext } from '../contexts/AppContext';
import FileUploader from '../components/FileUploader';
import { ContractUploadResponse, ContractUploadHistoryItem, ContractStatus } from '../types';

const triggerDownload = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export default function ContractUploadPage(): JSX.Element {
  const { apiService, showNotification } = useAppContext();

  const [status, setStatus] = useState<ContractStatus | null>(null);
  const [history, setHistory] = useState<ContractUploadHistoryItem[]>([]);
  const [file, setFile] = useState<File[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewResult, setPreviewResult] = useState<ContractUploadResponse | null>(null);
  const [showSkipped, setShowSkipped] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchPageData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([
        apiService.getContractStatus(),
        apiService.getContractUploadHistory()
      ]);
      setStatus(s);
      setHistory(h);
    } catch (err: any) {
      showNotification(err.message || 'Failed to load contract data.', 'error');
    } finally {
      setLoading(false);
    }
  }, [apiService, showNotification]);

  useEffect(() => { fetchPageData(); }, [fetchPageData]);

  const handleDownloadTemplate = async () => {
    try {
      const blob = await apiService.downloadContractTemplate();
      triggerDownload(blob, 'contracts_upload_template.csv');
    } catch (err: any) {
      showNotification(err.message || 'Failed to download template.', 'error');
    }
  };

  const handleExportCurrent = async () => {
    try {
      showNotification('Downloading current contract data...', 'info');
      const blob = await apiService.exportContracts();
      triggerDownload(blob, `contracts_backup_${new Date().toISOString().split('T')[0]}.csv`);
    } catch (err: any) {
      showNotification(err.message || 'Failed to export contracts.', 'error');
    }
  };

  const handleValidate = async () => {
    if (file.length === 0) return;
    setUploading(true);
    setPreviewResult(null);
    try {
      const result = await apiService.uploadContracts(file[0], true);
      setPreviewResult(result);
      if (result.rows_skipped > 0) setShowSkipped(true);
    } catch (err: any) {
      showNotification(err.message || 'Validation failed.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmUpload = async () => {
    setConfirmOpen(false);
    setUploading(true);
    try {
      const result = await apiService.uploadContracts(file[0], false);
      setPreviewResult(null);
      setFile([]);
      showNotification(`${result.rows_imported.toLocaleString()} contracts imported successfully!`, 'success');
      fetchPageData();
    } catch (err: any) {
      showNotification(err.message || 'Upload failed.', 'error');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Paper sx={{ p: { xs: 2, md: 3 } }} elevation={3}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Contract Master Data</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Upload contract data to update the master table. This will replace all existing contract records.
      </Typography>

      {/* Status Bar */}
      {status && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
          <strong>{status.total_contracts.toLocaleString()}</strong> contracts currently loaded
          {status.last_updated_at && <> · Last updated: {new Date(status.last_updated_at).toLocaleString()}</>}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button variant="outlined" startIcon={<TemplateIcon />} onClick={handleDownloadTemplate}>Download Template</Button>
        <Button variant="outlined" startIcon={<BackupIcon />} onClick={handleExportCurrent} disabled={!status?.total_contracts}>Download Current Data</Button>
      </Box>

      {/* File Upload */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, maxWidth: 600 }}>
        <FileUploader
          title="Upload Contract File"
          description="Upload a CSV, XLSX, or XLS file with contract data."
          onFilesSelected={(files) => { setFile(files); setPreviewResult(null); }}
          onFileRemove={() => { setFile([]); setPreviewResult(null); }}
          acceptedFileTypes={{
            'text/csv': ['.csv'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'application/vnd.ms-excel': ['.xls']
          }}
          allowMultiple={false}
          files={file}
        />
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleValidate}
            disabled={file.length === 0 || uploading}
            startIcon={uploading && !previewResult ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
          >
            {uploading && !previewResult ? 'Validating...' : 'Validate File'}
          </Button>
          {previewResult && previewResult.rows_imported > 0 && (
            <Button
              variant="contained"
              color="success"
              onClick={() => setConfirmOpen(true)}
              disabled={uploading}
            >
              Confirm Upload
            </Button>
          )}
        </Box>
      </Paper>

      {/* Preview Results */}
      {previewResult && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Validation Preview</Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Chip icon={<SuccessIcon />} label={`${previewResult.rows_imported.toLocaleString()} valid rows`} color="success" variant="outlined" />
            {previewResult.rows_skipped > 0 && (
              <Chip icon={<WarningIcon />} label={`${previewResult.rows_skipped} skipped`} color="warning" variant="outlined" />
            )}
          </Box>
          {previewResult.rows_skipped > 0 && (
            <>
              <Button size="small" onClick={() => setShowSkipped(!showSkipped)} endIcon={showSkipped ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
                {showSkipped ? 'Hide' : 'Show'} skipped details
              </Button>
              <Collapse in={showSkipped}>
                <TableContainer sx={{ maxHeight: 300, mt: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewResult.skipped_details.map((d, i) => (
                        <TableRow key={i}>
                          <TableCell>{d.row}</TableCell>
                          <TableCell>{d.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </>
          )}
        </Paper>
      )}

      {/* Upload History */}
      {history.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Upload History</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Uploaded By</TableCell>
                  <TableCell>File</TableCell>
                  <TableCell align="right">Imported</TableCell>
                  <TableCell align="right">Skipped</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell>{new Date(h.uploaded_at).toLocaleString()}</TableCell>
                    <TableCell>{h.uploaded_by}</TableCell>
                    <TableCell>{h.file_name}</TableCell>
                    <TableCell align="right">{h.rows_imported.toLocaleString()}</TableCell>
                    <TableCell align="right">{h.rows_skipped > 0 ? <Chip label={h.rows_skipped} color="warning" size="small" /> : 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Contract Upload</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will <strong>replace all existing contract records</strong> with {previewResult?.rows_imported.toLocaleString()} new records
            {previewResult?.rows_skipped ? ` (${previewResult.rows_skipped} rows will be skipped)` : ''}.
            <br /><br />
            Make sure you have downloaded a backup of the current data before proceeding.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmUpload} variant="contained" color="error" disabled={uploading}>
            {uploading ? <CircularProgress size={24} /> : 'Replace All Contracts'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
